import React, { useState } from 'react';
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Grid,
  Divider,
  Button,
  TextField,
  Box,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import StarIcon from '@mui/icons-material/Star';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import DrawIcon from '@mui/icons-material/Draw';
import api from '../../services/api';
import DigitalSignatureCapture from '../signatures/DigitalSignatureCapture';
import DocumentHeader from '../documents/DocumentHeader';
import LegalClausesSection from '../documents/LegalClausesSection';
import DocumentFooter from '../documents/DocumentFooter';
import {
  renderMarkdown,
  SectionWrapper,
  SectionHeader,
  FieldLabel,
  CardField,
  DOCUMENT_COLORS,
  APPBAR_GRADIENT,
} from '../documents/documentStyles';

const VerifierHireDocumentPreview = ({ open, onClose, data, onSubmit }) => {
  const [editingSection, setEditingSection] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [verifierTier, setVerifierTier] = useState(data.verifierTier || 'professional');
  const [improvingField, setImprovingField] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);

  const handleSendClick = () => {
    setShowSignatureDialog(true);
  };

  const handleSignatureComplete = (signatureData) => {
    setShowSignatureDialog(false);
    onSubmit({ ...data, ...editedData, verifierTier, signature: signatureData });
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImproveWithAI = async (field) => {
    const currentValue = editedData[field] || data[field] || '';
    if (!currentValue.trim()) {
      showSnackbar('Please enter some content first', 'warning');
      return;
    }

    setImprovingField(field);
    try {
      const response = await api.post('/bids/ai/improve-section', {
        section: field,
        currentContent: currentValue,
        improvementRequest: 'Make this more professional and detailed for a verification request document'
      });

      setEditedData({ ...editedData, [field]: response.data.data.improvedContent });
      showSnackbar('Content improved with AI!', 'success');
    } catch (error) {
      console.error('Error improving content:', error);
      showSnackbar('Failed to improve content. Please try again.', 'error');
    } finally {
      setImprovingField(null);
    }
  };

  const handleStartEdit = (section) => {
    setEditingSection(section);
    setEditedData({ ...data, verifierTier });
  };

  const handleSaveEdit = () => {
    Object.assign(data, editedData);
    data.verifierTier = verifierTier;
    setEditingSection(null);
  };

  const handleCancelEdit = () => {
    setEditedData({});
    setVerifierTier(data.verifierTier || 'professional');
    setEditingSection(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (date) => {
    if (!date) return 'TBD';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderEditableText = (section, field, value, multiline = false) => {
    const isEditing = editingSection === section;
    const isImproving = improvingField === field;

    if (isEditing) {
      return (
        <Box>
          <TextField
            fullWidth
            multiline={multiline}
            rows={multiline ? 4 : 1}
            value={editedData[field] || value || ''}
            onChange={(e) => setEditedData({ ...editedData, [field]: e.target.value })}
            variant="outlined"
            size="small"
            disabled={isImproving}
          />
          {multiline && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Enhance this text with AI">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isImproving ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={() => handleImproveWithAI(field)}
                  disabled={isImproving}
                  sx={{
                    textTransform: 'none',
                    borderColor: DOCUMENT_COLORS.brand,
                    color: DOCUMENT_COLORS.brand,
                    '&:hover': {
                      borderColor: DOCUMENT_COLORS.brandDark,
                      backgroundColor: 'rgba(37, 99, 235, 0.04)'
                    }
                  }}
                >
                  {isImproving ? 'Improving...' : 'Improve with AI'}
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    }

    // Display mode: use markdown renderer for multiline
    if (multiline && value) {
      return (
        <Box
          sx={{ cursor: 'pointer', minHeight: '24px' }}
          onClick={() => handleStartEdit(section)}
        >
          {renderMarkdown(value)}
        </Box>
      );
    }

    return (
      <Typography
        variant="body1"
        sx={{ cursor: 'pointer', minHeight: '24px', fontWeight: 500 }}
        onClick={() => handleStartEdit(section)}
      >
        {value || 'Click to edit'}
      </Typography>
    );
  };

  const getTierInfo = () => {
    if (verifierTier === 'professional') {
      return {
        name: 'Professional Verifier',
        description: 'Detailed technical inspections, comprehensive reports, advanced certifications',
        requirements: [
          'Advanced certifications and professional licenses',
          '5+ years of verification experience',
          'Specialized expertise in project type',
          'Comprehensive E&O insurance coverage',
          'Advanced testing and measurement equipment',
          'Certified comprehensive reporting capability'
        ],
        color: '#1D4ED8',
        icon: <StarIcon />
      };
    } else {
      return {
        name: 'General Verifier',
        description: 'Basic inspections, visual assessments, standard compliance checks',
        requirements: [
          'Basic safety certifications',
          '1-3 years of verification experience',
          'General industry knowledge',
          'Basic professional liability coverage',
          'Standard inspection tools',
          'Summary report capability'
        ],
        color: '#2563EB',
        icon: <VerifiedUserIcon />
      };
    }
  };

  const tierInfo = getTierInfo();

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{ style: { background: DOCUMENT_COLORS.dialogBg } }}
    >
      <AppBar position="sticky" elevation={0} sx={{ background: APPBAR_GRADIENT }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, ml: 2, fontWeight: 600 }}>
            Verification Request - {data.projectTitle || 'Untitled Project'}
          </Typography>
          <Button
            color="inherit"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            Print
          </Button>
          <Button
            color="inherit"
            startIcon={<DownloadIcon />}
            sx={{ mr: 1, textTransform: 'none' }}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            sx={{
              background: '#fff',
              color: DOCUMENT_COLORS.brand,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { background: '#F0F4FF' }
            }}
            onClick={handleSendClick}
            startIcon={<DrawIcon />}
          >
            Sign & Send to Verifiers
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>

          {/* Document Header */}
          <DocumentHeader
            documentType="VERIFICATION REQUEST"
            issuer={{
              name: data.contractorName,
            }}
            date={formatDate(new Date())}
            meta={{ 'Response Deadline': formatDate(data.responseDeadline) }}
          />

          <Divider sx={{ mb: 4 }} />

          {/* 1. Verifier Tier Selection */}
          <SectionWrapper>
            <SectionHeader
              icon={<VerifiedUserOutlinedIcon fontSize="small" />}
              title="1. Verifier Tier & Level Required"
              editing={editingSection === 'tier'}
              onEdit={() => handleStartEdit('tier')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                value={verifierTier}
                exclusive
                onChange={(e, newTier) => {
                  if (newTier !== null) {
                    setVerifierTier(newTier);
                    if (editingSection === 'tier') {
                      setEditedData({ ...editedData, verifierTier: newTier });
                    }
                  }
                }}
                fullWidth
                disabled={editingSection !== 'tier' && editingSection !== null}
              >
                <ToggleButton value="general" sx={{ p: 2 }}>
                  <Box textAlign="left" width="100%">
                    <Box display="flex" alignItems="center" mb={1}>
                      <VerifiedUserIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">General Verifier</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Basic inspections, visual assessments, standard compliance checks
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      1-3 years experience &bull; Basic certifications
                    </Typography>
                  </Box>
                </ToggleButton>
                <ToggleButton value="professional" sx={{ p: 2 }}>
                  <Box textAlign="left" width="100%">
                    <Box display="flex" alignItems="center" mb={1}>
                      <StarIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Professional Verifier</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Detailed technical inspections, comprehensive reports, advanced certifications
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      5+ years experience &bull; Professional licenses &bull; Specialized expertise
                    </Typography>
                  </Box>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Paper sx={{ p: 2, bgcolor: '#f9f9f9', border: `2px solid ${tierInfo.color}`, borderRadius: 1 }}>
              <Box display="flex" alignItems="center" mb={2}>
                {tierInfo.icon}
                <Typography variant="h6" sx={{ ml: 1, color: tierInfo.color }}>
                  {tierInfo.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {tierInfo.description}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }} gutterBottom>
                Minimum Requirements:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
                {tierInfo.requirements.map((req, index) => (
                  <Typography key={index} variant="body2" component="li">
                    {req}
                  </Typography>
                ))}
              </Box>
            </Paper>
          </SectionWrapper>

          {/* 2. Project Overview */}
          <SectionWrapper>
            <SectionHeader
              icon={<InfoOutlinedIcon fontSize="small" />}
              title="2. Project Overview"
              editing={editingSection === 'overview'}
              onEdit={() => handleStartEdit('overview')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FieldLabel>Project Title</FieldLabel>
                {renderEditableText('overview', 'projectTitle', data.projectTitle)}
              </Grid>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Verification Type</FieldLabel>
                  {renderEditableText('overview', 'verificationType', data.verificationType)}
                </CardField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Location</FieldLabel>
                  {renderEditableText('overview', 'location', data.location)}
                </CardField>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Project Summary</FieldLabel>
                {renderEditableText('overview', 'projectSummary', data.projectSummary, true)}
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* 3. Verification Scope */}
          <SectionWrapper>
            <SectionHeader
              icon={<SearchOutlinedIcon fontSize="small" />}
              title="3. Verification Scope & Requirements"
              editing={editingSection === 'scope'}
              onEdit={() => handleStartEdit('scope')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <FieldLabel>Areas to be Verified</FieldLabel>
            {renderEditableText('scope', 'areasToVerify', data.areasToVerify, true)}
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Specific Focus Points</FieldLabel>
            {renderEditableText('scope', 'focusPoints', data.focusPoints, true)}
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Compliance Standards Required</FieldLabel>
            {renderEditableText('scope', 'complianceStandards', data.complianceStandards, true)}
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Building Codes & Regulations</FieldLabel>
            {renderEditableText('scope', 'buildingCodes', data.buildingCodes, true)}
          </SectionWrapper>

          {/* 4. Project Context */}
          <SectionWrapper>
            <SectionHeader
              icon={<HistoryEduOutlinedIcon fontSize="small" />}
              title="4. Project Context & Background"
              editing={editingSection === 'context'}
              onEdit={() => handleStartEdit('context')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <FieldLabel>Project Stage</FieldLabel>
            {renderEditableText('context', 'projectStage', data.projectStage)}
            <Box sx={{ mt: 2 }}>
              <FieldLabel>Verification Purpose</FieldLabel>
              {renderEditableText('context', 'verificationPurpose', data.verificationPurpose, true)}
            </Box>
            <Box sx={{ mt: 2 }}>
              <FieldLabel>Key Stakeholders</FieldLabel>
              {renderEditableText('context', 'stakeholders', data.stakeholders, true)}
            </Box>
          </SectionWrapper>

          {/* 5. Timeline */}
          <SectionWrapper>
            <SectionHeader
              icon={<ScheduleOutlinedIcon fontSize="small" />}
              title="5. Timeline & Schedule"
              editing={editingSection === 'timeline'}
              onEdit={() => handleStartEdit('timeline')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Verification Start Date</FieldLabel>
                  {renderEditableText('timeline', 'startDate', formatDate(data.startDate))}
                </CardField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CardField>
                  <FieldLabel>Expected Duration</FieldLabel>
                  {renderEditableText('timeline', 'duration', data.duration)}
                </CardField>
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Report Delivery Deadline</FieldLabel>
                {renderEditableText('timeline', 'reportDeadline', formatDate(data.reportDeadline))}
              </Grid>
              <Grid item xs={12}>
                <FieldLabel>Timeline Urgency</FieldLabel>
                <Chip
                  label={data.timelineUrgency || 'Standard'}
                  color={data.timelineUrgency === 'Urgent' ? 'error' : 'primary'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>
            </Grid>
          </SectionWrapper>

          {/* 6. Expected Deliverables */}
          <SectionWrapper>
            <SectionHeader
              icon={<AssignmentOutlinedIcon fontSize="small" />}
              title="6. Expected Deliverables"
              editing={editingSection === 'deliverables'}
              onEdit={() => handleStartEdit('deliverables')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Typography variant="body2" sx={{ mb: 1.5, color: '#475569' }}>
              Verifiers must provide the following deliverables:
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 2.5, m: 0,
                '& li': { mb: 1, color: '#334155', lineHeight: 1.7, '&::marker': { color: DOCUMENT_COLORS.brand } },
              }}
            >
              {verifierTier === 'professional' ? (
                <>
                  <Typography variant="body2" component="li">Comprehensive detailed inspection report</Typography>
                  <Typography variant="body2" component="li">Technical analysis with measurements and testing results</Typography>
                  <Typography variant="body2" component="li">Photographic documentation with annotations</Typography>
                  <Typography variant="body2" component="li">Compliance certification against all relevant codes</Typography>
                  <Typography variant="body2" component="li">Detailed findings with severity classifications</Typography>
                  <Typography variant="body2" component="li">Corrective action recommendations with priorities</Typography>
                  <Typography variant="body2" component="li">Professional certification and seal</Typography>
                  <Typography variant="body2" component="li">Follow-up consultation as needed</Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" component="li">Basic inspection summary report</Typography>
                  <Typography variant="body2" component="li">Visual assessment findings</Typography>
                  <Typography variant="body2" component="li">Photographic documentation</Typography>
                  <Typography variant="body2" component="li">Compliance checklist</Typography>
                  <Typography variant="body2" component="li">Key findings and observations</Typography>
                  <Typography variant="body2" component="li">Basic recommendations</Typography>
                </>
              )}
            </Box>
            <Box sx={{ mt: 2 }}>
              <FieldLabel>Additional Deliverables Required</FieldLabel>
              {renderEditableText('deliverables', 'additionalDeliverables', data.additionalDeliverables, true)}
            </Box>
          </SectionWrapper>

          {/* 7. Supporting Documentation */}
          <SectionWrapper>
            <SectionHeader
              icon={<FolderOutlinedIcon fontSize="small" />}
              title="7. Supporting Documentation"
              editing={editingSection === 'documentation'}
              onEdit={() => handleStartEdit('documentation')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <FieldLabel>Documents Provided to Verifier</FieldLabel>
            {renderEditableText('documentation', 'documentsProvided', data.documentsProvided, true)}
            <Divider sx={{ my: 2 }} />
            <FieldLabel>Site Access & Logistics</FieldLabel>
            {renderEditableText('documentation', 'siteAccess', data.siteAccess, true)}
          </SectionWrapper>

          {/* 8. Verifier Qualifications */}
          <SectionWrapper>
            <SectionHeader
              icon={<SchoolOutlinedIcon fontSize="small" />}
              title="8. Verifier Qualifications & Requirements"
              editing={editingSection === 'requirements'}
              onEdit={() => handleStartEdit('requirements')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <Paper sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} gutterBottom>
                Required Qualifications for {tierInfo.name}:
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.5 } }}>
                {tierInfo.requirements.map((req, index) => (
                  <Typography key={index} variant="body2" component="li">
                    {req}
                  </Typography>
                ))}
              </Box>
            </Paper>
            <Box sx={{ mt: 2 }}>
              <FieldLabel>Additional Requirements</FieldLabel>
              {renderEditableText('requirements', 'additionalRequirements', data.additionalRequirements, true)}
            </Box>
          </SectionWrapper>

          {/* 9. Selection Criteria */}
          <SectionWrapper>
            <SectionHeader
              icon={<ChecklistOutlinedIcon fontSize="small" />}
              title="9. Selection Criteria & Evaluation"
              editing={editingSection === 'selection'}
              onEdit={() => handleStartEdit('selection')}
              onSave={handleSaveEdit}
              onCancel={handleCancelEdit}
            />
            <CardField>
              <FieldLabel>Number of Verifiers to Hire</FieldLabel>
              {renderEditableText('selection', 'numberOfVerifiers', data.numberOfVerifiers?.toString() || '1')}
            </CardField>
            <Box sx={{ mt: 2 }}>
              <FieldLabel>Evaluation Criteria (in order of importance)</FieldLabel>
              {renderEditableText('selection', 'evaluationCriteria', data.evaluationCriteria, true)}
            </Box>
          </SectionWrapper>

          {/* 10. Legal Terms & Conditions */}
          <LegalClausesSection
            documentType="verifier_hire"
            sectionNumber={10}
            additionalTerms={data.termsAndConditions}
          />

          <DocumentFooter documentType="verification request" />
        </Paper>
      </Container>

      {/* Digital Signature Capture */}
      <DigitalSignatureCapture
        open={showSignatureDialog}
        onClose={() => setShowSignatureDialog(false)}
        onSignatureComplete={handleSignatureComplete}
        documentType="verifier_hire"
        documentData={data}
        signerName={data.contractorName || data.clientName || ''}
        signerEmail={data.contractorEmail || data.clientEmail || ''}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default VerifierHireDocumentPreview;
